import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { NavigationExtras } from '@angular/router';
import { MutationService } from '@app/core/graphql/mutation/mutation.service';
import { QueryService } from '@app/core/graphql/query/query.service';
import { State } from '@app/core/ngrx/reducers';
import { applicationSelectors, userSelectors } from '@app/core/ngrx/selectors';
import { ApplicationService } from '@app/core/services/application/application.service';
import { ModalService } from '@app/core/services/modal/modal.service';
import { ToastService } from '@app/core/services/toast/toast.service';
import { ToolService } from '@app/core/services/tool/tool.service';
import { UserService } from '@app/core/services/user/user.service';
import { WarningService } from '@app/core/services/warning/warning.service';
import { hasHousing, isNil } from '@app/shared/utils';
import { faGreaterThan, faLessThanEqual } from '@fortawesome/pro-regular-svg-icons';
import { faAlignLeft, faCheckCircle, faFile, faFileSignature, faInfoCircle, faShoppingCart, faSignature, faUpload } from '@fortawesome/pro-solid-svg-icons';
import { AlertController, NavController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { forkJoin } from 'rxjs';
import { flatMap } from 'rxjs/operators';

@Component({
    selector: 'app-application-chain-document-list',
    templateUrl: './application-chain-document-list.component.html',
    styleUrls: ['./application-chain-document-list.component.scss'],
})
export class ApplicationChainDocumentListComponent implements OnChanges {
    faGreaterThan = faGreaterThan;
    faUpload = faUpload;
    faFile = faFile;
    faInfoCircle = faInfoCircle;
    faCheckCircle = faCheckCircle;
    faAlignLeft = faAlignLeft;
    faFileSignature = faFileSignature;
    faSignature = faSignature;
    faShoppingCart = faShoppingCart;

    @Input() chainedApplications;
    @Input() profile;
    @Input() applicationChainState;

    $USER_ID = this.store.select(userSelectors.getUserID);

    $CLINICAL_DOCUMENT_LIST = this.store.select(applicationSelectors.getClinicalDocumentList);

    USER_ID: string;

    preApprovalDocs: any;

    customDocuments = [];

    preapprovalDocuments = {
        resume: false,
        education: false,
        english: false,
        passport: false,
        visa: false,
        immunization: false,
        healthInsurance: false,
        malpractice: false,
        housing: false,
        contract: false,
    };

    customResume = false;
    customEducation = false;

    preApprovalRequirementList: any = [];

    applicationChainId = '';

    defaultDocuments = false;

    customRequirementMalpracticeCheck = false;

    requirementsList: any[];

    loading = true;

    CA_MISSING_FLAG = false;

    applicationContractList: any[] = [];

    nonVirtualApplications: any[] = [];

    handsOnApplications: any[] = [];

    downloadInvitationalLetters: any[] = [];

    applicationHealthInsuranceDocuments: any[] = [];

    applicationHealthInsurance: any[] = [];

    showPreRequirements = false;
    showPostRequirements = false;
    showHousingCopyCheckboxes = false;

    constructor(
        private modalService: ModalService,
        private toastService: ToastService,
        public toolService: ToolService,
        private queryService: QueryService,
        private store: Store<State>,
        private applicationService: ApplicationService,
        private navCtrl: NavController,
        private alertController: AlertController,
        private mutationService: MutationService,
        private warning: WarningService,
        private userService: UserService
    ) {
        this.listener();
    }

    ngOnChanges() {
        this.toolService.content.subscribe((a) => {/* empty */});

        if (this.chainedApplications.length > 0) {
            this.chainedApplications.forEach((a) => {
                if (a.hashousing) {
                    this.profile.housing = true;
                }

                if (a.states !== 'Pending' && a.states !== 'Pending-Booked' && a.states !== 'Pre-Approved') {
                    if (this.applicationContractList.length < this.chainedApplications.length) {
                        this.applicationContractList.push(a);
                    }
                }
            });

            this.nonVirtualApplications = [...this.chainedApplications]
                .filter((h) => {
                    if (!(h.type.includes('Online') || h.type.includes('Virtual'))) {
                        return h;
                    }
                })
                .map((h) => {
                    return {
                        id: h.id,
                        name: h.name,
                        hashousing: h.hashousing,
                        housingaddress: h.housingaddress,
                        housingtype: h.housingtype,
                        airbnblink: h.airbnblink,
                        passportnumber: h.User.passportnumber,
                    };
                });

            if (this.nonVirtualApplications.some((a) => a.hashousing)) {
                this.showHousingCopyCheckboxes = true;
            }

            this.queryService.applicationChainDetails(this.chainedApplications[0].ApplicationChainMap.applicationchainid).subscribe(
                (data: any) => {
                    this.loading = false;
                    const query = data;

                    const applicationsMalpracticeDocuments = [...query.data.applicationChainMalpracticeDocument.documents];

                    const applicationHealthInsuranceDocuments = [...query.data.applicationChainHealthInsuranceDocument.documents];

                    this.configureHandsOnApplications(applicationsMalpracticeDocuments);
                    this.configureHousingApplications(applicationHealthInsuranceDocuments);

                    // submitted requirements that have already been submitted from other applications set
                    if (query.data.submittedChainedProgramDocuments && query.data.submittedChainedProgramDocuments.documents) {
                        query.data.submittedChainedProgramDocuments.documents.forEach((a) => {
                            if (isNil(this.profile[a])) {
                                this.profile[a] = true;
                            }
                        });
                    }

                    if (query.data.applicationRequirementList && query.data.applicationRequirementList.documents.length) {
                        // ========== Put business/filter logic in graphql -> query -> applicationRequirementList function when possible ==========
                        if (this.applicationChainState !== 'Reserved' && this.applicationChainState !== 'Enrolled') {
                            if (this.applicationChainState === 'Pending') {
                                this.showPreRequirements = true;
                            } else if (this.applicationContractList.length !== 0 && this.applicationChainState === 'Accepted') {
                                this.showPreRequirements = false;
                            }
                            this.requirementsList = [...query.data.applicationRequirementList.documents]
                                .map((m) => {
                                    return {
                                        applicationRequirementId: m['ApplicationRequirements.applicationRequirementId'],
                                        documentId: m['ApplicationRequirements.Documents.id'],
                                        Document: {
                                            key: m['ApplicationRequirements.Documents.uri'],
                                            filename: m['Documents.filename'],
                                        },
                                        level: m.level,
                                        levelid:
                                            m.programid !== null ? m.programid : m.departmentid !== null ? m.departmentid : m.hostid !== null ? m.hostid : null,
                                        programName: m['Program.name']
                                            ? m['Program.name']
                                            : m['Host.Programs.name']
                                            ? m['Host.Programs.name']
                                            : m['Department.Hosts.Programs.name']
                                            ? m['Department.Hosts.Programs.name']
                                            : null,
                                        jsondata: m['ApplicationRequirements.jsondata'],
                                    };
                                })
                                .filter((f) => {
                                    return f.jsondata.preapproval;
                                });
                            this.preApprovalRequirementList = [...this.requirementsList].map((a) => {
                                return {
                                    name: a.jsondata.name,
                                    type: a.jsondata.type,
                                    id: a.applicationRequirementId,
                                };
                            });
                        } else {
                            // show post requirements after at all application contracts are signed
                            if (this.chainedApplications.every((a) => a.signedcontract)) {
                                this.showPreRequirements = true;
                                // this.showPostRequirements = true;
                                this.requirementsList = [...query.data.applicationRequirementList.documents].map((m) => {
                                    this.profile[m['ApplicationRequirements.applicationRequirementId']];
                                    return {
                                        applicationRequirementId: m['ApplicationRequirements.applicationRequirementId'],
                                        documentId: m['ApplicationRequirements.Documents.id'],
                                        Document: {
                                            key: m['ApplicationRequirements.Documents.uri'],
                                            filename: m['Documents.filename'],
                                        },
                                        level: m.level,
                                        levelid:
                                            m.programid !== null ? m.programid : m.departmentid !== null ? m.departmentid : m.hostid !== null ? m.hostid : null,
                                        programName: m['Program.name']
                                            ? m['Program.name']
                                            : m['Host.Programs.name']
                                            ? m['Host.Programs.name']
                                            : m['Department.Hosts.Programs.name']
                                            ? m['Department.Hosts.Programs.name']
                                            : null,
                                        jsondata: m['ApplicationRequirements.jsondata'],
                                    };
                                });
                            } else {
                                // only show contract requirements
                                this.showPostRequirements = false;
                            }
                        }
                    }

                    this.applicationChainId = this.chainedApplications[0].ApplicationChainMap.applicationchainid;

                    this.profile.immunizations = [...query.data.user.immunizations.immunizationList.documents];

                    if (this.profile) {
                        this.profile.resume = this.profile.item1name === null ? false : this.profile.item1name.indexOf('Temporary') !== -1 ? false : true;
                        this.profile.passport = this.profile.item2name === null ? false : true;
                        this.profile.malpractice = this.profile.item3name === null ? false : true;
                        this.profile.education = this.profile.item4name === null ? false : true;
                        this.profile.letterofrecc = this.profile.item6name === null ? false : true;
                        this.profile.visaproof = this.profile.item7name === null ? false : true;
                        this.profile.healthinsurance = this.profile.item8name === null ? false : true;
                        this.profile.toefl = this.profile.item10name === null ? false : true;
                        this.profile.hashousing = hasHousing(this.chainedApplications[0]);
                        this.profile.signedcontract = this.chainedApplications[0].signedcontract !== null ? true : false;
                        this.preApprovalRequirementList.forEach((a) => {
                            a['uploaded'] = true ? this.profile[a.type] === true || this.profile[a.id] : false;
                        });
                    }
                    // User Documents Process -- Invitation Letter, Purchased Malpractice
                    if (this.profile.applicationChainUserDocuments && this.profile.applicationChainUserDocuments.length) {
                        this.downloadInvitationalLetters = [];
                        this.profile.applicationChainUserDocuments.forEach((b) => {
                            if (b.data.applicationDocuments && b.data.applicationDocuments.documents.length) {
                                const readyDocuments = b.data.applicationDocuments;

                                // find invitation letter url and program id for display
                                readyDocuments.documents.some((doc) => {
                                    if (doc.filename.indexOf('Invitation_Letter') !== -1) {
                                        const splitUrl = doc.url.split('/');

                                        const applicationid = splitUrl.slice(-2)[0];

                                        const matchingApplications = this.chainedApplications.find((a) => a.id === applicationid);

                                        this.downloadInvitationalLetters.push({
                                            url: doc.url,
                                            programid: matchingApplications.Program.id,
                                        });
                                    }
                                    if (doc.filename.indexOf('Malpractice_Certificate') !== -1) {
                                        const splitUrl = doc.url.split('/');

                                        const applicationId = splitUrl.slice(-3)[0];
                                        this.handsOnApplications = [...this.handsOnApplications].map((filteredApplication) => {
                                            if (filteredApplication.id === applicationId) {
                                                return {
                                                    ...filteredApplication,
                                                    purchasedMalpracticeUrl: doc.url,
                                                };
                                            } else {
                                                return filteredApplication;
                                            }
                                        });
                                    }
                                });
                            }
                        });
                    }

                    // call pre approval trigger if items have already been submitted from previous application
                    setTimeout(() => {
                        if (
                            this.applicationChainState === 'Pending' &&
                            !this.chainedApplications.every(
                                (a) => a.preacceptancereviewable && this.preApprovalRequirementList.every((b) => b.uploaded === true)
                            )
                        ) {
                            this.triggerPreApprovedNotification();
                        }
                    }, 100);
                },
                (err) => {
                    // console.log('getting error', err);
                }
            );
        }
    }

    listener() {
        //
        this.$USER_ID.subscribe((userid: string) => (this.USER_ID = userid));
        //
        //
        this.$CLINICAL_DOCUMENT_LIST.subscribe((clinical_document_list: any) => {
            //
            // console.log('CLINICAL DOCUMENT LIST', clinical_document_list);
            //
            if (clinical_document_list.length > 0) {
                let ca_document = clinical_document_list.find((d: any) => d.filename === 'clinical_agreement.pdf');
                if (!ca_document) {
                    this.CA_MISSING_FLAG = true;
                } else {
                    this.CA_MISSING_FLAG = false;
                }
            } else {
                this.CA_MISSING_FLAG = true;
            }
        });
    }

    upload(
        item: string,
        applicationRequirementId?: string,
        requirementName?: string,
        index?: number,
        levelType?: string,
        levelId?: string,
        applicationid?: string
    ) {
        const completeUserProfile = {
            id: this.chainedApplications[0].userid,
            name: this.chainedApplications[0].fullname,
            applicationid: applicationid || null,
        };
        const applicationidList = [...this.chainedApplications].map((a) => {
            return a.id;
        });
        const type =
            item === 'education'
                ? 'schoolrecords'
                : item === 'resume'
                ? 'cv'
                : item === 'visa'
                ? 'visaproof'
                : item === 'passport'
                ? 'passport'
                : item === 'english'
                ? 'toefl'
                : item === 'immunization'
                ? 'immune'
                : item === 'immune'
                ? 'immune'
                : item === 'healthInsurance'
                ? 'healthinsurance'
                : item === 'malpractice'
                ? 'malpractice'
                : 'application';

        this.modalService
            .upload(
                type, // requirement type
                completeUserProfile,
                null, // application id
                null, // download link
                requirementName,
                null, // user submission
                applicationRequirementId,
                applicationidList,
                { levelType: levelType, levelId: levelId }
            )
            .then((response: any) => {
                if (response.data.result) {
                    this.applicationService.enrollmentSubject.next(true);
                    this.successUpload();
                    if (response.data) {
                        switch (response.data.key) {
                            case 'cv':
                                this.profile.item1name = response.data.filename;
                                break;

                            case 'passport':
                                this.profile.item2name = response.data.filename;
                                break;

                            case 'malpractice':
                                this.profile.item3name = response.data.filename;
                                break;

                            case 'schoolrecords':
                                this.profile.item4name = response.data.filename;
                                break;

                            case 'letterofrecc':
                                this.profile.item6name = response.data.filename;
                                break;

                            case 'visaproof':
                                this.profile.item7name = response.data.filename;
                                break;

                            case 'healthinsurance':
                                this.callHealthInsurance();
                                break;

                            case 'toefl':
                                this.profile.item10name = response.data.filename;
                                break;

                            case 'immune':
                                this.profile.immunizations.push(response.data);
                            default:
                                break;
                        }
                        // general document uploaded status
                        this.profile[item] = true;
                        // additional requirement document uploaded status
                        this.profile[applicationRequirementId] = true;
                        this.preApprovalRequirementList.forEach((e) => {
                            if (e.name === this.profile[e.name]) {
                                e['uploaded'] = true;
                            } else if (e.id === this.profile[e.id]) {
                                e['uploaded'] = true;
                            } else if (response.data.key === 'schoolrecords' && e.type === 'education') {
                                e['uploaded'] = true;
                            } else if ((e.type === 'resume' && response.data.key === 'cv') || response.data.key === 'resume') {
                                e['uploaded'] = true;
                            }
                        });
                        this.successUpload();
                    }
                }
            });
    }
    // used for downloading then uploading a document requirement
    downloadThenUploadModal = (
        item?: string,
        key?: string,
        filename?: string,
        name?: string,
        requirementName?: string,
        usersubmission?: boolean,
        applicationRequirement_Id?: string,
        leveltype?: string,
        levelid?: string
    ) => {
        const completeUserProfile = {
            id: this.chainedApplications[0].userid,
            name: this.chainedApplications[0].fullname,
        };
        const applicationidList = [...this.chainedApplications].map((a) => {
            return a.id;
        });

        const type =
            item === 'education'
                ? 'schoolrecords'
                : item === 'resume'
                ? 'cv'
                : item === 'visa'
                ? 'visaproof'
                : item === 'passport'
                ? 'passport'
                : item === 'english'
                ? 'toefl'
                : item === 'immunization'
                ? 'immune'
                : item === 'immune'
                ? 'immune'
                : item === 'healthInsurance'
                ? 'healthinsurance'
                : item === 'malpractice'
                ? 'malpractice'
                : 'application';

        this.modalService
            .upload(type, completeUserProfile, null, key, name, usersubmission, applicationRequirement_Id, applicationidList, {
                levelType: leveltype,
                levelId: levelid,
            })
            .then((response: any) => {
                if (response.data.result) {
                    this.applicationService.enrollmentSubject.next(true);

                    if (response.data) {
                        switch (response.data.key) {
                            case 'cv':
                                this.profile.item1name = response.data.filename;
                                break;

                            case 'passport':
                                this.profile.item2name = response.data.filename;
                                break;

                            case 'malpractice':
                                this.profile.item3name = response.data.filename;
                                break;

                            case 'schoolrecords':
                                this.profile.item4name = response.data.filename;
                                break;

                            case 'letterofrecc':
                                this.profile.item6name = response.data.filename;
                                break;

                            case 'visaproof':
                                this.profile.item7name = response.data.filename;
                                break;

                            case 'healthinsurance':
                                break;
                            case 'toefl':
                                this.profile.item10name = response.data.filename;
                                break;

                            case 'immune':
                                this.profile.immunizations.push(response.data);
                            default:
                                break;
                        }
                        // general document uploaded status
                        this.profile[item] = true;
                        // additional requirement document uploaded status
                        this.profile[applicationRequirement_Id] = true;
                        this.preApprovalRequirementList.forEach((e) => {
                            if (e.name === this.profile[e.name]) {
                                e['uploaded'] = true;
                            } else if (e.id === this.profile[e.id]) {
                                e['uploaded'] = true;
                            } else if (response.data.key === 'schoolrecords' && e.type === 'education') {
                                e['uploaded'] = true;
                            }
                        });
                        this.successUpload();
                    }
                }
            });
    };

    uploadMalpractice(applicationId: string) {
        const completeUserProfile = {
            id: this.chainedApplications[0].userid,
            name: this.chainedApplications[0].fullname,
        };

        this.modalService.upload('malpractice', completeUserProfile, applicationId).then((response: any) => {
            this.queryService.applicationMalpracticeDocuments(applicationId).subscribe((malpracticeDocuments: any) => {
                this.configureHandsOnApplications(malpracticeDocuments.data.applicationMalpracticeDocument.documents, true);
                this.applicationService.enrollmentSubject.next(true);
            });
        });
    }

    callHealthInsurance() {
        this.queryService.getApplicationChainHealthInsuranceDocuments(this.applicationChainId).subscribe((healthInsuranceDocuments: any) => {
            this.configureHousingApplications(healthInsuranceDocuments.data.applicationChainHealthInsuranceDocument.documents, true);
        });
    }

    openHousingModal(applicationid: string, applicationName, index: number) {
        this.modalService
            .housing(applicationid, applicationName)
            .then((response: any) => {
                if (response.data.update) {
                    this.successHousing();
                    this.profile.housing = true;
                    this.nonVirtualApplications[index].hashousing = true;
                    this.showHousingCopyCheckboxes = true;
                    this.applicationService.enrollmentSubject.next(true);
                }
                if (response.data.form) {
                    this.nonVirtualApplications[index].housingaddress = response.data.form.housingaddress;
                    this.nonVirtualApplications[index].housingtype = response.data.form.housingtype;
                    this.nonVirtualApplications[index].airbnblink = response.data.form.airbnblink;
                    this.applicationService.enrollmentSubject.next(true);
                }
            })
            .catch((err: Error) => {/* empty */});
    }

    requestInvitationLetter() {
        const applicationId = this.chainedApplications.map((a) => a.id);

        forkJoin(
            applicationId.map((id) => {
                return this.applicationService.requestInvitation(id);
            })
        ).subscribe((a: any) => {
            this.modalService.successfulAction({ message: a[0].message });
        });
    }

    openPassportModal(applicationid: string) {
        this.modalService
            .passport(applicationid)
            .then((response: any) => {
                if (response.data.update) {
                    this.toastService.create({
                        message: 'Passport information submitted.',
                        duration: 3000,
                        position: 'top',
                        buttons: [
                            {
                                text: 'Close',
                                role: 'cancel',
                                handler: () => {
                                    console.log('Cancel clicked');
                                },
                            },
                        ],
                        closeButtonText: 'Done',
                        color: 'primary',
                    });
                    this.profile.passportform = true;
                    this.applicationService.enrollmentSubject.next(true);
                    this.requestInvitationLetter();
                }
            })
            .catch((err: Error) => {
                // console.log(err);
            });
    }

    downloadInvitationLetter(file: string) {
        window.open(file, '_blank');
    }

    successHousing() {
        this.toastService.create({
            message: 'Housing information submitted.',
            duration: 3000,
            position: 'top',
            buttons: [
                {
                    text: 'Close',
                    role: 'cancel',
                    handler: () => {
                        console.log('Cancel clicked');
                    },
                },
            ],
            closeButtonText: 'Done',
            color: 'primary',
        });
    }

    openContract(applicationid: string, token: string, signedcontract) {
        if (token) {
            this.viewClinicalAgreement(applicationid, token, signedcontract);
        } else {
            this.signClinicalAgreement(applicationid, this.applicationChainId);
        }
    }

    openHousingSuggestionsModal() {
        this.modalService
            .housingSuggestions(this.USER_ID)
            .then((response: any) => {
                if (response.data.update) {
                    this.successHousing();
                    this.profile.hashousing = true;
                    this.applicationService.enrollmentSubject.next(true);
                }
            })
            .catch((err: Error) => {
                // console.log(err);
            });
    }

    purchaseMalpractice(applicationid: string, applicationName?: string) {
        this.modalService
            .purchaseMalpractice(applicationid, applicationName)
            .then((res: any) => {
                if (res.data.purchased) {
                    this.modalService
                        .successfulPayment('malpractice')
                        .then((response: any) => {
                            location.reload();
                            this.applicationService.enrollmentSubject.next(true);
                        })
                        .catch((err: Error) => {
                            // console.log(err);
                            location.reload();
                        });
                }
            })
            .catch((err: Error) => {
                // console.log('stripe modal err: ', err);
            });
    }

    viewMalpracticeFile(url: string) {
        window.open(url, '_blank');
    }

    viewClinicalAgreement(applicationid: string, token: any, signedcontract: string) {
        //
        if (this.CA_MISSING_FLAG && token) {
            //
            const update = {
                applicationid: applicationid,
                userid: this.USER_ID,
                signatureId: token,
            };
            //
            this.applicationService
                .helloSignServerless(update)
                .pipe(
                    flatMap((response: any) => {
                        return this.applicationService.helloSignProccessApplication(update);
                    }),
                    flatMap((response) => {
                        return this.applicationService.getViewClinicalAgreement(applicationid, this.USER_ID);
                    })
                )
                .subscribe(async (res) => {
                    window.open(res.redirect, '_blank');
                    location.reload();
                });
        } else {
            if (signedcontract) {
                this.applicationService.getViewClinicalAgreement(applicationid, this.USER_ID).subscribe((res) => {
                    window.open(res.redirect, '_blank');
                });
            } else {
                this.signClinicalAgreement(applicationid, this.applicationChainId);
            }
        }
    }

    signClinicalAgreement = (applicationid: string, applicationchainid?: string) => {
        //
        const navigationExtras: NavigationExtras = {
            queryParams: {
                applicationId: applicationid,
                userId: this.USER_ID,
                chainid: applicationchainid,
            },
        };
        //
        this.navCtrl.navigateForward([`application/${applicationid}/clinical-agreement`], navigationExtras);
    };

    copyHousing(applicationId: string, index: number) {
        let count = 0;
        let housingOptions = [];

        this.nonVirtualApplications.forEach((a) => {
            if (a.hashousing === true) {
                count = count += 1;
                housingOptions.push({
                    hashousing: a.hashousing,
                    housingaddress: a.housingaddress,
                    housingtype: a.housingtype,
                    airbnblink: a.airbnblink,
                });
            }
        });
        const checkForHousingDups = [...housingOptions].filter((v, i, a) => a.findIndex((t) => JSON.stringify(t) === JSON.stringify(v)) === i);

        if (checkForHousingDups.length < 2) {
            const firstHousingSubmitted = this.nonVirtualApplications.find((a) => a.hashousing);
            if (firstHousingSubmitted) {
                if (firstHousingSubmitted.housingaddress) {
                    const copiedHousingInfo = {
                        housingaddress: firstHousingSubmitted.housingaddress,
                        housingtype: firstHousingSubmitted.housingtype,
                        airbnblink: firstHousingSubmitted.airbnblink,
                        hashousing: true,
                    };
                    // make mutation request to update application
                    this.updateHousingMutation(copiedHousingInfo, applicationId, index);
                    this.applicationService.enrollmentSubject.next(true);
                }
            } else {
                this.warning.create('Something went wrong when getting your housing info. Please try fill out the housing form.');
            }
        } else {
            this.presentHousingOptions(housingOptions, applicationId, index);
        }
    }

    presentHousingOptions = async (params: any, applicationId: string, index: number) => {
        const alert = await this.alertController.create({
            cssClass: 'housing-selection-alert',
            header: 'Where are you staying?',
            inputs: [...params].map((p, i) => {
                const parsedAddress = typeof p.housingaddress === 'string' ? JSON.parse(p.housingaddress) : p.housingaddress;
                return {
                    housingaddress: p.housingaddress,
                    housingtype: p.housingtype,
                    airbnblink: p.airbnblink,
                    type: 'radio',
                    value: p,
                    label: `${parsedAddress[0].address}\n${parsedAddress[0].city}, ${parsedAddress[0].state} ${parsedAddress[0].postal}\n${p.housingtype}\n${p.airbnblink}`,
                    handler: () => {/* empty */},
                };
            }),
            buttons: [
                {
                    text: 'Cancel',
                    role: 'cancel',
                    cssClass: 'secondary',
                    handler: () => {/* empty */},
                },
                {
                    text: 'Ok',
                    role: 'submit',
                    handler: () => {/* empty */},
                },
            ],
        });

        await alert.present();

        await alert.onDidDismiss().then((result) => {
            if (result.data.values && result.role === 'submit') {
                this.updateHousingMutation(result.data.values, applicationId, index);
            }
        });
    };

    updateHousingMutation(updateObject: any, applicationId: string, index: number) {
        this.mutationService.updateHousing(updateObject, applicationId).subscribe((response: any) => {
            if (response.data.updateApplication.success) {
                this.nonVirtualApplications[index].hashousing = true;
                this.nonVirtualApplications[index].housingaddress = updateObject.housingaddress;
                this.nonVirtualApplications[index].housingtype = updateObject.housingtype;
                this.nonVirtualApplications[index].airbnblink = updateObject.airbnblink;
            }
            this.applicationService.enrollmentSubject.next(true);
        });
    }

    successUpload() {
        this.toastService
            .create({
                message: 'Document uploaded.',
                duration: 3000,
                position: 'top',
                buttons: [
                    {
                        text: 'Close',
                        role: 'cancel',
                        handler: () => {
                            console.log('Cancel clicked');
                        },
                    },
                ],
                closeButtonText: 'Done',
                color: 'primary',
            })
            .then(() => {
                setTimeout(() => {
                    this.triggerPreApprovedNotification();
                }, 2000);
            });
    }

    triggerPreApprovedNotification() {
        if (this.applicationChainState === 'Pending') {
            if (this.preApprovalRequirementList.every((item: any) => item.uploaded === true)) {
                this.applicationService.requestPreAcceptanceReviewChain(this.applicationChainId).subscribe((response: any) => {/* empty */});
            }
        }
    }

    viewDocument(requirement: any, url?: string) {
        let request;

        if (url && url.includes('https')) {
            window.open(url, '_blank');
            return;
        }

        if (requirement.immunization) {
            request = {
                userid: this.USER_ID,
                immunizationFileName: requirement.file,
                immunization: true,
            };
        } else {
            request = {
                userid: this.USER_ID,
                applicationRequirementId: requirement.applicationRequirementId,
                type: requirement.jsondata.type,
                level: requirement.level,
                levelId: requirement.levelid,
                name: requirement.jsondata.name,
            };
        }

        this.userService.getUploadedDocuments(request).subscribe((res: any) => {
            if (res.success && res.url) {
                window.open(res.url, '_blank');
            } else {
                this.warning.create('We were unable to process this request. Please contact support for assistance.');
            }
        });
    }

    configureHandsOnApplications(applicationDocuments: any, upload?: boolean) {
        if (upload) {
            this.handsOnApplications = [...this.handsOnApplications]
                .filter((application) => {
                    return application.type === 'Hands-On';
                })
                .map((b) => {
                    if (
                        applicationDocuments.some((t) => {
                            return t.applicationid === b.id;
                        })
                    ) {
                        const index = applicationDocuments.findIndex((d) => d.applicationid === b.id);
                        const url = applicationDocuments[index].url;

                        const filename = applicationDocuments[index].filename;

                        let application = { ...b };

                        application.url = url;
                        application.fileName = filename;

                        return application;
                    } else {
                        return b;
                    }
                });
        } else {
            this.handsOnApplications = [...this.chainedApplications]
                .filter((application) => {
                    return application.type === 'Hands-On';
                })
                .map((b) => {
                    if (
                        applicationDocuments.some((t) => {
                            return t.applicationid === b.id;
                        })
                    ) {
                        const index = applicationDocuments.findIndex((d) => d.applicationid === b.id);
                        const url = applicationDocuments[index].url;

                        const filename = applicationDocuments[index].filename;

                        let application = { ...b };

                        application.url = url;
                        application.fileName = filename;

                        return application;
                    } else {
                        return b;
                    }
                });
        }
    }

    configureHousingApplications(documents: any, upload?: boolean) {
        if (upload) {
            this.applicationHealthInsurance = [...this.applicationHealthInsurance]
                // .filter((application) => {
                //     return application.type === 'Hands-On';
                // })
                .map((b) => {
                    let application = { ...b };

                    let applicationFoundDocuments = [];

                    documents.forEach((doc) => {
                        if (
                            doc.some((t) => {
                                return t.applicationid === b.id;
                            })
                        ) {
                            applicationFoundDocuments = [...doc];
                        }
                    });

                    application.documents = applicationFoundDocuments;

                    return application;
                });
        } else {
            this.applicationHealthInsurance = [...this.chainedApplications]
                .filter((application) => {
                    return !application.type.includes('Virtual');
                })
                .map((b) => {
                    let application = { ...b };

                    let applicationFoundDocuments = [];

                    documents.forEach((doc) => {
                        if (
                            doc.some((t) => {
                                return t.applicationid === b.id;
                            })
                        ) {
                            applicationFoundDocuments = [...doc];
                        }
                    });

                    application.documents = applicationFoundDocuments;

                    return application;
                });
        }
    }
}
